/** Request feedback belongs to one visit/operation/attempt; cancellation is not recovery. */
import type { PageError, PageErrorName } from './page-state'

export type FeedbackToken = { visit: string; operation: string; attempt: number }
export type RequestFeedback = { attempt: number; pending: boolean; error: PageError | null }
export type FeedbackState = Readonly<Record<string, RequestFeedback>>
export type FeedbackPolicy = {
  failure: string
  success?: string
  location?: 'form' | 'product' | 'fields' | 'deletion' | 'submit'
  blocksSubmission?: boolean
}
export type RequestFailure = {
  blocksSubmission?: boolean
  operation: string
  /** The merchant wording the Worker sent on this error, or undefined when the request carried none (a network blip
   *  that never reached the Worker, or a browser-side integrity error). The surface fills it from its own server-owned
   *  words for that state (the builder: `readiness.onReadFailure`); the app never substitutes a constant of its own. */
  message: string | undefined
  pending: boolean
  location: 'form' | 'product' | 'fields' | 'deletion' | 'submit'
  /** The named error kind, so recovery is chosen by what went wrong (retry a network blip, load the saved version on a conflict), not by a fixed button. */
  kind: PageErrorName
}

export function beginRequestFeedback(state: FeedbackState, token: FeedbackToken): FeedbackState {
  return {
    ...state,
    [token.operation]: { attempt: token.attempt, pending: true, error: state[token.operation]?.error ?? null },
  }
}

export function settleRequestFeedback(
  state: FeedbackState,
  visit: string,
  token: FeedbackToken,
  outcome: 'accepted' | 'cancelled' | PageError,
): FeedbackState {
  const before = state[token.operation]
  if (token.visit !== visit || before?.attempt !== token.attempt || !before.pending) return state
  let error = before.error
  if (outcome === 'accepted') error = null
  else if (outcome !== 'cancelled') error = outcome
  return { ...state, [token.operation]: { ...before, pending: false, error } }
}

export function requestFailures(
  actions: Record<string, { feedback?: FeedbackPolicy }>,
  state: FeedbackState,
): RequestFailure[] {
  return Object.entries(actions).flatMap(([operation, action]) => {
    const result = state[operation]
    if (!action.feedback || !result?.error) return []
    return [
      {
        operation,
        // The Worker chose the wording and sent it on the error; the app renders it, and never a constant of its own.
        // When the request carried no server message (a network blip before any response, or a browser-side integrity
        // error), `message` is undefined and the surface fills it from its own server-owned words for that state — the
        // builder's `readiness.onReadFailure` (`StudyBuilderRequestFailures`).
        message: result.error.message,
        pending: result.pending,
        location: action.feedback.location ?? 'form',
        blocksSubmission: action.feedback.blocksSubmission,
        kind: result.error.name,
      },
    ]
  })
}
